import React, { useContext } from 'react';
import { EventStateEnum, PlannedEventOutDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilDataTable, { ColumnConfig, PagedData } from '@components/RingilDataTable/RingilDataTable';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz';
import EntityLink from '../EntityLink';
import { useRuleApi } from '../useRuleApi';
import { useTriggerLabel } from './useTriggerLabel';

interface ComponentProps {
  companyId?: string;
}

/**
 * What the robot is about to do, soonest first.
 *
 * This is the answer to "why hasn't it done anything yet" — usually because `notBefore` is still
 * in the future. Only unfinished events appear; processed ones are history and live in the
 * execution log.
 */
const PlannedTable: React.FC<ComponentProps> = ({ companyId }) => {
  const { tr } = useTranslationLgs();
  const { listPlanned } = useRuleApi(companyId);
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const triggerLabel = useTriggerLabel();

  const columnConfig: ColumnConfig<PlannedEventOutDTO>[] = [
    {
      id: 'notBefore',
      header: 'AiDispatcher.notBefore',
      headerDefault: 'Runs at',
      verticalAlign: 'middle',
      render: row => (
        <div className="flex flex-col">
          <span>{stringDateTimeWithLocaleFromApiStringInTz(row.notBefore, loggedUser?.defaultTimezoneId)}</span>
          {new Date(row.notBefore).getTime() > Date.now() && <span className="text-xs text-content-tertiary">{tr('AiDispatcher.waitingOnDelay', 'Waiting on a delay')}</span>}
        </div>
      ),
    },
    {
      id: 'entity',
      header: 'AiDispatcher.affectedEntity',
      headerDefault: 'Expedition',
      verticalAlign: 'middle',
      render: row => <EntityLink entityType={row.entityType} entityId={row.entityId} entityLabel={row.entityLabel} />,
    },
    {
      id: 'trigger',
      header: 'AiDispatcher.trigger',
      headerDefault: 'Trigger',
      verticalAlign: 'middle',
      render: row => <span>{triggerLabel(row.triggerType)}</span>,
    },
    {
      id: 'state',
      header: 'AiDispatcher.eventState',
      headerDefault: 'State',
      width: '10rem',
      verticalAlign: 'middle',
      render: row => (
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${row.eventState === EventStateEnum.CLAIMED ? 'bg-green-100 text-green-800' : 'bg-base-tertiary text-content-secondary'}`}>
          {row.eventState === EventStateEnum.CLAIMED ? tr('AiDispatcher.eventClaimed', 'Running') : tr('AiDispatcher.eventPending', 'Queued')}
        </span>
      ),
    },
    {
      id: 'attempts',
      header: 'AiDispatcher.attempts',
      headerDefault: 'Attempts',
      width: '9rem',
      verticalAlign: 'middle',
      // More than one means a previous run was interrupted and the event was recovered.
      render: row => <span>{row.attempts > 1 ? tr('AiDispatcher.attemptsRecovered', '{{count}} (recovered)', { count: row.attempts }) : row.attempts}</span>,
    },
  ];

  const loadDataFromApi = (_filter: unknown, pageable: { pageNumber?: number; pageSize?: number }, onSuccess: (paged: PagedData<PlannedEventOutDTO>) => void) => {
    listPlanned(pageable, data => onSuccess(data));
  };

  return <RingilDataTable columnConfig={columnConfig} loadDataFromApi={loadDataFromApi} filterName="aiDispatcherPlanned" enableFilterV2={false} />;
};

export default PlannedTable;
